import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Settings } from 'lucide-react';
import { EditPanel } from '../EditPanel/EditPanel';
import { EditField } from '../EditPanel/types';
import { useEditMode } from '../../utils/useEditMode';
import { useWidgetEditSuppressed } from '../../utils/WidgetEditSuppressContext';
import colors from '../../colors/colors';
import { ThemeFontValue, ThemeIconValue } from '../../api/widgetThemeApi';
import { dispatchPanelOpened, subscribeToPanelOpened } from '../../utils/editPanelEvents';
import { ConfirmDialog } from '../ConfirmDialog';
import { useWidgetTheme } from '../../hooks/useWidgetTheme';

type IconPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface IconPositionStyle {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

/**
 * Calculate the best position for the edit icon based on available viewport space
 */
const calculateIconPosition = (
  wrapperRect: DOMRect,
  iconSize: number = 32,
  gap: number = 4,
): { position: IconPosition; style: IconPositionStyle } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const isMobile = viewportWidth < 768; // md breakpoint

  // Calculate available space on each side
  const spaceLeft = wrapperRect.left;
  const spaceRight = viewportWidth - wrapperRect.right;
  const spaceTop = wrapperRect.top;
  const spaceBottom = viewportHeight - wrapperRect.bottom;

  // Minimum space needed to place icon outside (icon size + small gap)
  const minSpace = iconSize + gap;

  // Determine horizontal position
  const canPlaceLeft = spaceLeft >= minSpace;
  const canPlaceRight = spaceRight >= minSpace;

  // Determine vertical position
  const canPlaceTop = spaceTop >= minSpace;
  const canPlaceBottom = spaceBottom >= minSpace;

  // On mobile, prefer right side to avoid overlapping with sidebar buttons
  // Priority on mobile: top-right > bottom-right > top-left > bottom-left > inside
  // Priority on desktop: top-left > top-right > bottom-left > bottom-right > inside
  let position: IconPosition = 'top-left';
  let style: IconPositionStyle = {};

  if (isMobile) {
    // Mobile: prefer right side
    if (canPlaceRight && canPlaceTop) {
      position = 'top-right';
      style = {
        top: wrapperRect.top - gap,
        left: wrapperRect.right - iconSize + gap,
      };
    } else if (canPlaceRight && canPlaceBottom) {
      position = 'bottom-right';
      style = {
        top: wrapperRect.bottom - iconSize + gap,
        left: wrapperRect.right - iconSize + gap,
      };
    } else if (canPlaceLeft && canPlaceTop) {
      position = 'top-left';
      style = {
        top: wrapperRect.top - gap,
        left: wrapperRect.left - gap,
      };
    } else if (canPlaceLeft && canPlaceBottom) {
      position = 'bottom-left';
      style = {
        top: wrapperRect.bottom - iconSize + gap,
        left: wrapperRect.left - gap,
      };
    } else {
      // Fallback: inside top-right
      position = 'top-right';
      style = {
        top: wrapperRect.top + gap,
        left: wrapperRect.right - iconSize - gap,
      };
    }
  } else {
    // Desktop: prefer left side (original behavior)
    if (canPlaceLeft && canPlaceTop) {
      position = 'top-left';
      style = {
        top: wrapperRect.top - gap,
        left: wrapperRect.left - gap,
      };
    } else if (canPlaceRight && canPlaceTop) {
      position = 'top-right';
      style = {
        top: wrapperRect.top - gap,
        left: wrapperRect.right - iconSize + gap,
      };
    } else if (canPlaceLeft && canPlaceBottom) {
      position = 'bottom-left';
      style = {
        top: wrapperRect.bottom - iconSize + gap,
        left: wrapperRect.left - gap,
      };
    } else if (canPlaceRight && canPlaceBottom) {
      position = 'bottom-right';
      style = {
        top: wrapperRect.bottom - iconSize + gap,
        left: wrapperRect.right - iconSize + gap,
      };
    } else {
      // Fallback: inside top-left with small inset
      position = 'top-left';
      style = {
        top: wrapperRect.top + gap,
        left: wrapperRect.left + gap,
      };
    }
  }

  return { position, style };
};

interface WidgetWrapperProps {
  children: React.ReactNode;
  widgetId?: string;
  displayName?: string;
  editFields: EditField[];
  editValues: Record<string, any>;
  onEditChange: (key: string, value: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
  /** Set of field keys that have saved (non-default) values */
  savedKeys?: Set<string>;
  /** Set of field keys locked by Concierge Studio */
  studioLockedKeys?: Set<string>;
  /** Reset a single field to its component default */
  onResetField?: (key: string) => void;
  themeColors?: Record<string, string>;
  themeFonts?: Record<string, ThemeFontValue>;
  themeIcons?: Record<string, ThemeIconValue>;
  /** 'hover' (default): icon appears on hover. 'persistent': icon always visible in edit mode. */
  triggerMode?: 'hover' | 'persistent';
  /** Icon to show. 'pencil' (default) or 'gear' for layout widgets. */
  triggerIcon?: 'pencil' | 'gear';
  /** Fixed corner position for persistent mode. */
  triggerPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Optional label text shown next to icon in persistent mode (e.g., "Edit Home Layout"). */
  triggerLabel?: string;
  /** Optional inline styles applied to the wrapper div (e.g. to constrain width in flex layouts). */
  wrapperStyle?: React.CSSProperties;
  /** Optional extra CSS class name(s) appended to the wrapper div. */
  containerClassName?: string;
  /** Optional pixel offset applied to the auto-calculated edit icon position (useful for small widgets). */
  iconOffset?: { top?: number; left?: number };
}

export const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  children,
  widgetId,
  displayName,
  editFields,
  editValues,
  onEditChange,
  onSave,
  isSaving = false,
  savedKeys,
  studioLockedKeys,
  onResetField,
  themeColors = {},
  themeFonts = {},
  themeIcons = {},
  triggerMode = 'hover',
  triggerIcon = 'pencil',
  triggerPosition = 'bottom-right',
  triggerLabel,
  wrapperStyle,
  containerClassName,
  iconOffset,
}) => {
  // Auto-derive studioLockedKeys from theme if not explicitly passed
  const { data: _themeData } = useWidgetTheme();
  const effectiveStudioLockedKeys = useMemo(() => {
    if (studioLockedKeys) return studioLockedKeys;
    const sck = (_themeData as any)?.studioControlledKeys?.widgetConfig;
    if (!sck || !widgetId) return undefined;
    const keys = sck[widgetId];
    return keys?.length ? new Set<string>(keys) : undefined;
  }, [studioLockedKeys, _themeData, widgetId]);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [screenPosition, setScreenPosition] = useState({ x: 0, y: 0 });
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [showExternalCloseDialog, setShowExternalCloseDialog] = useState(false);
  const [iconPosition, setIconPosition] = useState<IconPositionStyle | null>(null);
  const [isWidgetHovered, setIsWidgetHovered] = useState(false);
  const [isIconHovered, setIsIconHovered] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingButtonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isEditMode = useEditMode();
  const isSuppressed = useWidgetEditSuppressed();

  // Combined hover state - show icon if widget OR icon is hovered, but hide when panel is open
  // Persistent mode: always show (unless panel is open)
  const shouldShowIcon =
    triggerMode === 'persistent' ? !isPanelOpen : (isWidgetHovered || isIconHovered) && !isPanelOpen;

  // Update icon position when wrapper is hovered or panel is open
  const updateIconPosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();

      if (triggerMode === 'persistent') {
        // Persistent mode: place at a fixed corner inside the widget
        const gap = 12;
        const btnHeight = 32;
        // Estimate width: icon-only = 28px, labeled = ~160px
        const estimatedWidth = triggerLabel ? 160 : 28;
        const pos: IconPositionStyle = {};
        if (triggerPosition.includes('top')) {
          pos.top = rect.top + gap;
        } else {
          pos.top = rect.bottom - btnHeight - gap;
        }
        if (triggerPosition.includes('right')) {
          pos.left = rect.right - estimatedWidth - gap;
        } else {
          pos.left = rect.left + gap;
        }
        setIconPosition(pos);
      } else {
        const { style } = calculateIconPosition(rect);
        // Apply optional offset for small widgets
        if (iconOffset) {
          if (iconOffset.top) style.top = (style.top ?? 0) + iconOffset.top;
          if (iconOffset.left) style.left = (style.left ?? 0) + iconOffset.left;
        }
        setIconPosition(style);
      }
    }
  }, [triggerMode, triggerPosition, iconOffset]);

  // Update position on hover/scroll/resize
  useEffect(() => {
    if (!isEditMode || !shouldShowIcon) {
      setIconPosition(null);
      return;
    }

    updateIconPosition();

    // Update on scroll/resize
    const handleUpdate = () => updateIconPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isEditMode, shouldShowIcon, updateIconPosition]);

  // Check if values have changed from initial
  const isDirty = useCallback(() => {
    if (!initialValues) return false;
    return JSON.stringify(editValues) !== JSON.stringify(initialValues);
  }, [editValues, initialValues]);

  // Subscribe to panel opened events to close this panel when another opens
  useEffect(() => {
    if (!widgetId) return;

    const unsubscribe = subscribeToPanelOpened((openedWidgetId) => {
      // Close this panel if a different widget's panel was opened
      if (openedWidgetId !== widgetId && isPanelOpen) {
        if (isDirty()) {
          setShowExternalCloseDialog(true);
        } else {
          setIsPanelOpen(false);
        }
      }
    });

    return unsubscribe;
  }, [widgetId, isPanelOpen, isDirty]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  if (!isEditMode || isSuppressed) {
    return <>{children}</>;
  }

  const openPanel = () => {
    if (settingButtonRef.current) {
      const rect = settingButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Panel dimensions
      const panelWidth = 620;
      const panelHeight = 450;
      const gap = 10;

      // Calculate available space in each direction
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportWidth - rect.left;
      const spaceLeft = rect.right;

      // Determine vertical position
      let y: number;
      if (spaceBelow >= panelHeight + gap) {
        // Enough space below - position below button
        y = rect.bottom + gap;
      } else if (spaceAbove >= panelHeight + gap) {
        // Enough space above - position above button
        y = rect.top - panelHeight - gap;
      } else {
        // Not enough space in either direction - choose the side with more space
        if (spaceBelow >= spaceAbove) {
          y = Math.max(gap, viewportHeight - panelHeight - gap);
        } else {
          y = Math.max(gap, rect.top - panelHeight - gap);
        }
      }

      // Determine horizontal position
      let x: number;
      if (spaceRight >= panelWidth) {
        // Enough space on right - align left edge to button
        x = rect.left;
      } else if (spaceLeft >= panelWidth) {
        // Enough space on left - align right edge to button
        x = rect.right - panelWidth;
      } else {
        // Not enough space - center and clamp to viewport
        x = Math.max(gap, Math.min(viewportWidth - panelWidth - gap, rect.left));
      }

      // Final clamp to ensure panel stays within viewport
      x = Math.max(gap, Math.min(x, viewportWidth - panelWidth - gap));
      y = Math.max(gap, Math.min(y, viewportHeight - panelHeight - gap));

      setScreenPosition({ x, y });
    }
    if (!initialValues || JSON.stringify(editValues) === JSON.stringify(initialValues)) {
      setInitialValues({ ...editValues });
    }
    setIsPanelOpen(true);
    // Dispatch event to close other panels
    if (widgetId) {
      dispatchPanelOpened(widgetId);
    }
  };

  // Reset handler - restore values to initial state
  const handleReset = () => {
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        onEditChange(key, initialValues[key]);
      });
    }
  };

  // Save handler - calls onSave and updates initialValues to clear dirty state
  const handleSave = () => {
    if (onSave) {
      onSave();
    }
    // Update initialValues to current values so dirty state clears
    setInitialValues({ ...editValues });
  };

  // Handle external close dialog actions - reset values before closing
  const handleExternalDiscard = () => {
    handleReset();
    setShowExternalCloseDialog(false);
    setIsPanelOpen(false);
  };

  const handleExternalSave = () => {
    handleSave();
    setShowExternalCloseDialog(false);
    setIsPanelOpen(false);
  };

  const handleExternalCancel = () => {
    setShowExternalCloseDialog(false);
    setIsPanelOpen(false);
  };

  // Render the edit icon via portal for smart positioning
  const renderEditIcon = () => {
    if (!iconPosition) return null;

    const isPersistent = triggerMode === 'persistent';
    const IconComponent = triggerIcon === 'gear' ? Settings : Pencil;
    const hasLabel = isPersistent && triggerLabel;

    const iconElement = hasLabel ? (
      // Labeled button for persistent mode (more discoverable)
      <button
        ref={settingButtonRef}
        style={{
          position: 'fixed',
          top: iconPosition.top,
          left: iconPosition.left,
          height: 32,
          background: isIconHovered ? colors.blue_light : 'rgba(255, 255, 255, 0.92)',
          border: `1px solid ${isIconHovered ? colors.blue : 'rgba(0,0,0,0.12)'}`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '0 12px 0 8px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
          transition: 'all 0.2s ease',
          transform: isIconHovered ? 'scale(1.03)' : 'scale(1)',
          opacity: isIconHovered ? 1 : 0.85,
          zIndex: 999998,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          whiteSpace: 'nowrap',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          openPanel();
        }}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setIsIconHovered(true);
        }}
        onMouseLeave={() => setIsIconHovered(false)}
        aria-label={triggerLabel}
      >
        <IconComponent size={14} color={isIconHovered ? colors.blue : colors.gray_dark} />
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: isIconHovered ? colors.blue : colors.gray_dark,
            letterSpacing: '0.2px',
          }}
        >
          {triggerLabel}
        </span>
      </button>
    ) : (
      // Icon-only button (standard for hover mode, compact for persistent without label)
      <button
        ref={settingButtonRef}
        style={{
          position: 'fixed',
          top: iconPosition.top,
          left: iconPosition.left,
          width: isPersistent ? 28 : 32,
          height: isPersistent ? 28 : 32,
          background: isIconHovered ? colors.blue_light : isPersistent ? 'rgba(255, 255, 255, 0.85)' : colors.white,
          border: `1px solid ${isIconHovered ? colors.blue : isPersistent ? 'rgba(0,0,0,0.1)' : colors.gray_border}`,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: isPersistent ? '0 1px 4px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
          transition: 'background 0.2s, border-color 0.2s, transform 0.2s, opacity 0.2s',
          transform: isIconHovered ? 'scale(1.05)' : 'scale(1)',
          opacity: isPersistent && !isIconHovered ? 0.7 : 1,
          zIndex: 999998,
          padding: 0,
          minWidth: 0,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          openPanel();
        }}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setIsIconHovered(true);
        }}
        onMouseLeave={() => setIsIconHovered(false)}
        aria-label={triggerIcon === 'gear' ? 'Edit layout' : 'Edit widget'}
      >
        <IconComponent size={isPersistent ? 14 : 16} color={colors.gray_dark} />
      </button>
    );

    return ReactDOM.createPortal(iconElement, document.body);
  };

  return (
    <>
      <style>{`
        .widget-wrapper {
          position: relative;
          display: inline-block;
          width: 100%;
        
        }

        .widget-wrapper.widget-fill-container,
        .widget-wrapper.widget-fill-height {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }

        .widget-wrapper.widget-fill-height {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }

        .widget-wrapper.widget-hovered {
          outline: 1px dashed ${colors.blue};
        }

        .widget-wrapper.widget-editing {
          outline: 2px dashed ${colors.blue};
        }
      `}</style>

      <div
        ref={wrapperRef}
        className={`widget-wrapper${triggerMode === 'persistent' ? ' widget-fill-container' : ''}${containerClassName ? ` ${containerClassName}` : ''}${isPanelOpen ? ' widget-editing' : shouldShowIcon ? ' widget-hovered' : ''}`}
        style={wrapperStyle}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setIsWidgetHovered(true);
        }}
        onMouseLeave={() => {
          hoverTimeoutRef.current = setTimeout(() => {
            setIsWidgetHovered(false);
          }, 200);
        }}
      >
        {children}

        {isPanelOpen && (
          <EditPanel
            widgetId={widgetId}
            displayName={displayName}
            fields={editFields}
            values={editValues}
            initialValues={initialValues || undefined}
            onChange={onEditChange}
            onClose={() => setIsPanelOpen(false)}
            onReset={handleReset}
            screenPosition={screenPosition}
            onSave={handleSave}
            isSaving={isSaving}
            savedKeys={savedKeys}
            studioLockedKeys={effectiveStudioLockedKeys}
            onResetField={onResetField}
            themeColors={themeColors}
            themeFonts={themeFonts}
            themeIcons={themeIcons}
          />
        )}

        {showExternalCloseDialog && (
          <ConfirmDialog
            title="Unsaved Changes"
            message="You have unsaved changes. Would you like to save them before closing?"
            confirmLabel="Save"
            cancelLabel="Discard"
            onConfirm={handleExternalSave}
            onCancel={handleExternalDiscard}
            onDismiss={handleExternalCancel}
            isLoading={isSaving}
          />
        )}
      </div>

      {/* Render edit icon via portal for smart positioning */}
      {shouldShowIcon && renderEditIcon()}
    </>
  );
};
